/**
 * One implementation of "the client picks a receipt" for every screen with an attach button.
 *
 * The screen renders no input: the input is shared by the whole page (`services/receiptFileInput`).
 * The screen gets a press handler and the help flag, and hears back through its callbacks.
 *
 * The legacy `UploadPage` (default template) is not on this hook: its input belongs to
 * `react-drag-drop-files`, so the press and the cancel are not ours to observe. It keeps the safety
 * net (`prepareReceipt` never throws out) but not the pick-step events.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReceiptPrepareResult,
  prepareErrorTextKey,
  prepareReceipt,
} from '../services/receiptUpload';
import { PICK_HELP_AFTER } from '../services/receiptPick';
import {
  ReceiptFileReceiver,
  openReceiptPicker,
  registerReceiptReceiver,
} from '../services/receiptFileInput';
import { Transaction } from '../services/types';

export interface UseReceiptPickerOptions {
  uuid: string | undefined;
  transaction: Transaction | null | undefined;
  /** The materialised copy is ready — the screen stores it and enables its send button. */
  onFile: (file: File) => void;
  /** i18n key for what the client should read; the screen owns the wording and the styling. */
  onError: (textKey: string) => void;
  /** The read can take seconds on a cloud file, so the screen shows itself busy meanwhile. */
  onBusy: (busy: boolean) => void;
  /** A new attempt starts: the screen clears whatever the previous one left on the display. */
  onPickStart?: () => void;
}

export interface ReceiptPicker {
  /** Wire to the button. */
  open: () => void;
  /** The dialog produced nothing twice — show the client what to try. */
  helpVisible: boolean;
}

export const useReceiptPicker = ({
  uuid,
  transaction,
  onFile,
  onError,
  onBusy,
  onPickStart,
}: UseReceiptPickerOptions): ReceiptPicker => {
  const [fruitless, setFruitless] = useState(0);

  // Read through a ref so the receiver keeps one identity for the life of the screen.
  const handlers = useRef({ onFile, onError, onBusy, onPickStart, uuid, transaction });
  handlers.current = { onFile, onError, onBusy, onPickStart, uuid, transaction };

  const receiver = useRef<ReceiptFileReceiver>({
    fruitless: setFruitless,
    receive: (picked) => {
      const { onFile: file, onError: error, onBusy: busy, onPickStart: start } = handlers.current;
      start?.();
      busy(true);
      void (async () => {
        let prepared: ReceiptPrepareResult;
        try {
          prepared = await prepareReceipt(
            handlers.current.uuid,
            handlers.current.transaction,
            picked,
          );
        } catch {
          // `prepareReceipt` reports every failure it knows about and returns; reaching here means
          // something the browser does not support threw on the way. Without this the screen stayed
          // busy forever and nothing was written anywhere.
          prepared = { ok: false, file: null, error: 'unreadable' };
        } finally {
          busy(false);
        }
        if (!prepared.ok || !prepared.file) {
          error(prepareErrorTextKey(prepared.error, handlers.current.transaction?.merchant, Boolean(handlers.current.transaction?.concierge), picked));
          return;
        }
        file(prepared.file);
      })();
    },
  });

  useEffect(() => registerReceiptReceiver(receiver.current), []);

  const open = useCallback(() => {
    openReceiptPicker(receiver.current, handlers.current.transaction?.merchant, Boolean(handlers.current.transaction?.concierge));
  }, []);

  return { open, helpVisible: fruitless >= PICK_HELP_AFTER };
};

export default useReceiptPicker;
